import { useGraphqlPagination } from '@components/infiniteScroll';
import { Group } from '@components/layout';
import { Typography } from '@components/typography';
import {
  GetDatasetImageFilenamesQuery,
  useGetDatasetImageFilenamesQuery,
  useGetDatasetImageLazyQuery,
} from '@generated/graphql';
import { Container, Stack } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { DatasetsDetailsImagesSliderOverlayContextProvider } from './DatasetsDetailsImagesSliderOverlayContext';
import { DatasetsDetailsImagesSliderOverlayDetails } from './DatasetsDetailsImagesSliderOverlayDetails';
import { ImageSelector } from './ImageSelector';
import { ImageViewer } from './ImageViewer';
import { OverlayControls } from './OverlayControls';

const PRELOAD_COUNT = 5;

/**
 * Generates indices to preload in priority order (closest to current first)
 * Alternates between forward and backward from current position.
 * Example: currentIndex=5, count=5 → [6, 4, 7, 3, 8, 2, 9, 1, 10, 0]
 */
const getPreloadIndices = (currentIndex: number, maxIndex: number, count: number): number[] => {
  const indices: number[] = [];

  for (let offset = 1; offset <= count; offset += 1) {
    const forward = currentIndex + offset;
    if (forward <= maxIndex) {
      indices.push(forward);
    }

    const backward = currentIndex - offset;
    if (backward >= 0) {
      indices.push(backward);
    }
  }

  return indices;
};

type DatasetsDetailsImagesSliderProps = {
  imageIndex?: number | null;
  workspaceId?: string;
  datasetId?: string;
  datasetName?: string;
  defaultInferenceId?: string;
  runs?: number;
};

export const DatasetsDetailsImagesSlider = ({
  imageIndex: initialImageIndex = 0,
  workspaceId,
  datasetId,
  datasetName,
  defaultInferenceId,
  runs,
}: DatasetsDetailsImagesSliderProps) => {
  const { data: filenames, fetchMore } = useGraphqlPagination<
    GetDatasetImageFilenamesQuery,
    string
  >(
    useGetDatasetImageFilenamesQuery({
      variables: { workspaceId, datasetId, path: 'images/', limit: 100 },
    }),
    'getDatasetFiles',
  );

  const [imageIndex, setImageIndex] = useState(initialImageIndex);
  const [imageData, setImageData] = useState<Record<number, string>>({});
  const imageDataRef = useRef<Record<number, string>>({});
  const preloadSessionRef = useRef(0);

  const [getDatasetImage] = useGetDatasetImageLazyQuery();

  const filename = filenames?.[imageIndex] ?? null;
  const maxIndex = (runs ?? 0) - 1;

  // Store functions and arrays in refs to avoid triggering effect on reference changes
  const fetchMoreRef = useRef(fetchMore);
  const filenamesRef = useRef(filenames);
  const getDatasetImageRef = useRef(getDatasetImage);

  // Keep refs updated
  fetchMoreRef.current = fetchMore;
  filenamesRef.current = filenames;
  getDatasetImageRef.current = getDatasetImage;

  useEffect(() => {
    const currentFilenames = filenamesRef.current;
    if (!filename || !currentFilenames?.length) {
      return;
    }

    // Increment session ID to invalidate any in-progress preloads from previous navigation
    preloadSessionRef.current += 1;
    const currentSession = preloadSessionRef.current;

    const loadImage = async (index: number): Promise<boolean> => {
      // Check if session is still current (user hasn't navigated away)
      if (preloadSessionRef.current !== currentSession) {
        return false;
      }

      // Skip if already loaded
      if (imageDataRef.current[index]) {
        return true;
      }

      // Get current filenames from ref (may have been updated by fetchMore)
      const latestFilenames = filenamesRef.current;
      if (!latestFilenames) {
        return false;
      }

      // Check if we need to fetch more filenames
      if (index >= latestFilenames.length) {
        await fetchMoreRef.current();
        // After fetchMore, filenames array may have updated
        // Return false to skip this index for now - next effect run will retry
        return false;
      }

      // Check session again after any await
      if (preloadSessionRef.current !== currentSession) {
        return false;
      }

      const data = await getDatasetImageRef.current({
        variables: { workspaceId, datasetId, filename: latestFilenames[index] },
      });

      // Check session again after fetch
      if (preloadSessionRef.current !== currentSession) {
        return false;
      }

      const imageUrl = data?.data?.getDatasetImage?.data[0];
      if (imageUrl) {
        // Prefetch the actual image bytes using browser's Image object
        // This triggers the browser to download and cache the image
        await new Promise<void>((resolve) => {
          const img = new window.Image();
          img.onload = () => {
            resolve();
          };
          img.onerror = () => {
            resolve(); // Still resolve so we continue with other images
          };
          img.src = imageUrl;
        });

        // Check session again after prefetch
        if (preloadSessionRef.current !== currentSession) {
          return false;
        }

        imageDataRef.current = { ...imageDataRef.current, [index]: imageUrl };
        setImageData(imageDataRef.current);
      }

      return true;
    };

    const loadNextInQueue = async (indices: number[]): Promise<void> => {
      // Base case: no more indices to load
      if (indices.length === 0) {
        return;
      }

      // Check session before each load
      if (preloadSessionRef.current !== currentSession) {
        return;
      }

      const [nextIndex, ...remainingIndices] = indices;
      await loadImage(nextIndex);

      // Recursively load remaining indices
      await loadNextInQueue(remainingIndices);
    };

    const preloadImages = async () => {
      // First, load the current image (highest priority)
      await loadImage(imageIndex);

      // Check if session changed while loading current image
      if (preloadSessionRef.current !== currentSession) {
        return;
      }

      // Then load surrounding images in priority order (closest first)
      const preloadIndices = getPreloadIndices(imageIndex, maxIndex, PRELOAD_COUNT);
      await loadNextInQueue(preloadIndices);
    };

    void preloadImages();
  }, [imageIndex, filename, maxIndex, workspaceId, datasetId]);

  return (
    <DatasetsDetailsImagesSliderOverlayContextProvider
      datasetId={datasetId}
      filename={filename}
      initialInferenceId={defaultInferenceId}
      workspaceId={workspaceId}
    >
      <Stack
        data-cy="Datasets-Details-Images-Slider"
        sx={{ height: '100%', gap: 2, position: 'relative' }}
      >
        <Group alignItems="center" gap={4}>
          <Typography variant="subtitle1">{datasetName}</Typography>
          <ImageSelector
            filenames={filenames}
            selected={imageIndex}
            fetchMore={fetchMore}
            onSelect={setImageIndex}
          />
        </Group>
        <Group height="100%" width="100%" sx={{ containerType: 'size' }}>
          <ImageViewer
            imageIndex={imageIndex}
            url={imageData?.[imageIndex]}
            workspaceId={workspaceId}
            onPrevClick={imageIndex > 0 ? () => setImageIndex(imageIndex - 1) : undefined}
            onNextClick={imageIndex < runs - 1 ? () => setImageIndex(imageIndex + 1) : undefined}
          />
          <DatasetsDetailsImagesSliderOverlayDetails image={filenames?.[imageIndex] || ''} />
        </Group>
        <Container maxWidth="md" sx={{ mx: 'auto' }}>
          <OverlayControls />
        </Container>
      </Stack>
    </DatasetsDetailsImagesSliderOverlayContextProvider>
  );
};
